import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import { Tooltip } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainHeading from "../components/main-heading.component";
import { _get, _post } from "../services/api.service";
import "./Plans.css";

declare global {
  interface Window {
    Razorpay: any;
  }
}

const DEFAULT_FEATURES = [
  "10k credits",
  "Diversified AI voices",
  "Clone and use your own voice",
  "Custom images/videos in background",
  "Access to ready-made templates for social media",
  "Unlimited custom templates",
  "No watermark",
];

const DUMMY_PLAN = {
  external_plan_id: "demo_basic",
  name: "Basic",
  currency: "USD",
  price: "9.99",
  period: "monthly",
  description: "Get started with essential features for short-form video creation.",
};

const loadRazorpay = () =>
  new Promise<boolean>((resolve) => {
    if (document.getElementById("razorpay-script")) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.id = "razorpay-script";
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const getPeriodLabel = (period: string) => {
  if (period === "monthly") return "month";
  if (period === "yearly") return "year";
  return period;
};

const Plans = () => {
  const [plan, setPlan] = useState<any | null>(null);
  const [isDummy, setIsDummy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    _get("subscriptions/plans/")
      .then((data: any) => {
        if (!data || (Array.isArray(data) && data.length === 0)) {
          setPlan(DUMMY_PLAN);
          setIsDummy(true);
        } else {
          setPlan(Array.isArray(data) ? data[0] : data);
        }
      })
      .catch(() => {
        setPlan(DUMMY_PLAN);
        setIsDummy(true);
      });
  }, []);

  const openRazorpay = async (payload: any) => {
    const loaded = await loadRazorpay();
    if (!loaded) return;

    const options = {
      key: payload.key,
      subscription_id: payload.subscription_id,
      name: payload.name,
      description: payload.description,
      image: payload.image,
      prefill: payload.prefill,
      notes: payload.notes,
      theme: { color: "#2364e8" },

      handler: async (response: any) => {
        try {
          await _post("subscriptions/verify-payment/", response);
          navigate(
            `/payment-status?status=success&paymentId=${response.razorpay_payment_id}`,
          );
        } catch {
          navigate("/payment-status?status=failed&reason=verification_failed");
        }
      },
    };

    const rzp = new window.Razorpay(options);

    rzp.on("payment.failed", (res: any) => {
      navigate(
        `/payment-status?status=failed&reason=${
          res.error?.reason || "payment_failed"
        }`,
      );
    });

    rzp.open();
  };

  const handleSubscribe = async (plan: any) => {
    const response = await _post("subscriptions/create/", {
      plan_id: plan.external_plan_id,
    });

    if (response) {
      openRazorpay({
        key: response.key,
        subscription_id: response.subscription_id,
        name: "TheUnplan",
        description: plan.description,
        image: response.image,
        prefill: response.prefill,
        notes: { plan: plan.external_plan_id },
      });
    }
  };

  if (!plan) {
    return (
      <div className="plans-page">
        <div className="container-fluid">
          <MainHeading title="Plans" />
        </div>
        <div className="pl-loading">Loading plans...</div>
      </div>
    );
  }

  const currencySymbol = plan.currency === "INR" ? "₹" : "$";

  return (
    <div className="plans-page">
      <div className="container-fluid">
        <MainHeading
          title="Plans"
          subTitle="Unlock more credits, premium voices, and watermark-free exports."
        />
      </div>

      <div className="container-fluid pl-layout">
        <div className="pl-intro">
          <p className="pl-intro-text">
            One simple plan with everything you need to create polished short-form videos.
            Subscribe to start generating with higher limits and premium features.
          </p>
        </div>

        {isDummy && (
          <div className="pl-demo-banner">
            <ScienceOutlinedIcon />
            Demo — showing a sample plan. Connect your backend to see live pricing.
          </div>
        )}

        <div className="pl-card-wrap">
          <article className="pl-card">
            <div className="pl-badge">
              <span className="pl-badge-dot" aria-hidden />
              {plan.name}
            </div>

            <div className="pl-price-row">
              <span className="pl-currency">{currencySymbol}</span>
              <span className="pl-price">{plan.price}</span>
              <span className="pl-period">/ {getPeriodLabel(plan.period)}</span>
            </div>

            {plan.description && (
              <p className="pl-description">{plan.description}</p>
            )}

            <div className="pl-features">
              {DEFAULT_FEATURES.map((item, idx) => (
                <div key={item} className="pl-feature">
                  <span className="pl-feature-icon" aria-hidden>
                    <CheckCircleOutlineIcon />
                  </span>
                  <span className="pl-feature-text">{item}</span>
                  {idx === 0 && (
                    <Tooltip
                      title="1 credit equals 1 character"
                      arrow
                      enterTouchDelay={0}
                    >
                      <button
                        type="button"
                        className="pl-feature-help"
                        aria-label="Credits info"
                      >
                        <HelpOutlineIcon />
                      </button>
                    </Tooltip>
                  )}
                </div>
              ))}
            </div>

            <button
              type="button"
              className="pl-subscribe-btn"
              onClick={() => handleSubscribe(plan)}
            >
              <WorkspacePremiumOutlinedIcon />
              Subscribe now
            </button>

            <p className="pl-footnote">
              Secure checkout powered by Razorpay. Cancel anytime from subscription settings.
            </p>
          </article>
        </div>
      </div>
    </div>
  );
};

export default Plans;
